// Lightweight UI primitives. Tailwind-only, no external dep.
// Styled for a dark-mode-first modern legal app: indigo accent,
// slate neutrals, generous rounding, soft shadows.

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { Loader, AlertCircle, Info, CheckCircle, X } from 'lucide-react';
import { classNames } from '../../lib/formatters';

// ------------ Card ------------
export const Card = ({ className = '', children, ...rest }) => (
  <div
    className={classNames(
      'rounded-2xl border border-slate-200 bg-white shadow-sm',
      'dark:border-slate-800 dark:bg-slate-900',
      className,
    )}
    {...rest}
  >
    {children}
  </div>
);

export const CardHeader = ({ title, subtitle, action, className = '' }) => (
  <div className={classNames('flex items-start justify-between gap-4 px-6 py-4 border-b border-slate-100 dark:border-slate-800', className)}>
    <div>
      <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
      {subtitle && <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const CardBody = ({ className = '', children }) => (
  <div className={classNames('p-6', className)}>{children}</div>
);

// ------------ Button ------------
const buttonVariants = {
  primary: 'bg-indigo-600 text-white hover:bg-indigo-500 focus-visible:outline-indigo-600 shadow-sm',
  secondary: 'bg-white text-slate-900 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-100 dark:ring-slate-700 dark:hover:bg-slate-700',
  ghost: 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800',
  danger: 'bg-red-600 text-white hover:bg-red-500',
  success: 'bg-emerald-600 text-white hover:bg-emerald-500',
};

const buttonSizes = {
  sm: 'px-2.5 py-1.5 text-xs gap-1',
  md: 'px-3.5 py-2 text-sm gap-1.5',
  lg: 'px-5 py-2.5 text-sm gap-2',
};

export const Button = ({
  variant = 'primary',
  size = 'md',
  icon: Icon,
  loading = false,
  children,
  className = '',
  ...rest
}) => (
  <button
    className={classNames(
      'inline-flex items-center justify-center rounded-lg font-medium transition',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900',
      buttonVariants[variant],
      buttonSizes[size],
      className,
    )}
    {...rest}
  >
    {loading && <Loader size={14} className="animate-spin" />}
    {!loading && Icon && <Icon size={16} />}
    {children}
  </button>
);

// ------------ Input / Select / Textarea ------------
//
// `text-base sm:text-sm` on the font size is intentional and load-
// bearing for mobile UX: iOS Safari auto-zooms into any input whose
// font-size is < 16px the moment it receives focus, which means every
// single tap on a field would zoom the page in and force the user to
// pinch-zoom back out. text-base = 16px (no zoom). On tablet and
// desktop sm:text-sm drops back to the original 14px design.
const fieldBase =
  'block w-full rounded-lg border-0 bg-white px-3 py-2 text-base sm:text-sm text-slate-900 ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:bg-slate-800 dark:text-slate-100 dark:ring-slate-700 dark:placeholder:text-slate-500';

export const Label = ({ children, required, className = '' }) => (
  <label className={classNames('block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1', className)}>
    {children}{required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

export const Input = ({ className = '', ...rest }) => (
  <input className={classNames(fieldBase, className)} {...rest} />
);

export const Textarea = ({ className = '', rows = 3, ...rest }) => (
  <textarea className={classNames(fieldBase, className)} rows={rows} {...rest} />
);

export const Select = ({ options = [], className = '', placeholder, ...rest }) => (
  <select className={classNames(fieldBase, 'pr-8', className)} {...rest}>
    {placeholder !== undefined && <option value="">{placeholder}</option>}
    {options.map((o) => {
      if (typeof o === 'string') return <option key={o} value={o}>{o}</option>;
      return <option key={o.value} value={o.value}>{o.label}</option>;
    })}
  </select>
);

// ------------ Badge ------------
const badgeTones = {
  slate:   'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  blue:    'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300',
  indigo:  'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300',
  green:   'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300',
  amber:   'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
  red:     'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300',
  // Added for the role-tone palette in SettingsPage so the new
  // permission-bearing roles (Attorney / Clerk / IT) each get a
  // visually distinct badge instead of falling back to slate.
  sky:     'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300',
  emerald: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-200',
  purple:  'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300',
};
export const Badge = ({ tone = 'slate', children, className = '' }) => (
  <span className={classNames('inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium', badgeTones[tone] || badgeTones.slate, className)}>
    {children}
  </span>
);

// ------------ Empty state ------------
export const EmptyState = ({ icon: Icon = Info, title, description, action }) => (
  <div className="text-center py-12 px-6">
    <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
      <Icon size={24} className="text-slate-500 dark:text-slate-400" />
    </div>
    <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
    {description && <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-md mx-auto">{description}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

// ------------ Alert ------------
const alertTones = {
  info:    { wrap: 'bg-blue-50 text-blue-900 border-blue-200 dark:bg-blue-500/10 dark:text-blue-200 dark:border-blue-500/30', icon: Info },
  success: { wrap: 'bg-emerald-50 text-emerald-900 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-200 dark:border-emerald-500/30', icon: CheckCircle },
  warning: { wrap: 'bg-amber-50 text-amber-900 border-amber-200 dark:bg-amber-500/10 dark:text-amber-200 dark:border-amber-500/30', icon: AlertCircle },
  error:   { wrap: 'bg-red-50 text-red-900 border-red-200 dark:bg-red-500/10 dark:text-red-200 dark:border-red-500/30', icon: AlertCircle },
};
export const Alert = ({ tone = 'info', title, children, className = '' }) => {
  const t = alertTones[tone] || alertTones.info;
  const Icon = t.icon;
  return (
    <div className={classNames('flex gap-3 rounded-lg border p-3 text-sm', t.wrap, className)}>
      <Icon size={18} className="flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        {title && <p className="font-medium">{title}</p>}
        <div className={title ? 'mt-0.5 opacity-90' : ''}>{children}</div>
      </div>
    </div>
  );
};

// ------------ Modal ------------
export const Modal = ({ open, onClose, title, children, footer, size = 'md' }) => {
  if (!open) return null;
  const sizeClass = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' }[size];
  return (
    // Outer: items-end on mobile so the modal slides up from the bottom
    // like a native sheet — that's the pattern users expect on phones and
    // it keeps the modal anchored to the bottom of the visible area when
    // the iOS keyboard pops up (instead of getting punched off-screen by
    // the centered alignment trying to recompute against the shrunken
    // viewport). At sm+ we go back to the centered card.
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className={classNames(
        // Mobile: full-width sheet pinned to bottom with rounded top
        // corners only. sm+: floating card with full rounded corners.
        'relative w-full bg-white shadow-xl dark:bg-slate-900',
        'rounded-t-2xl sm:rounded-2xl',
        // Cap height to the dynamic viewport so the modal never
        // outgrows the visible area (incl. when the iOS keyboard is up).
        // Mobile uses dvh which tracks the keyboard / address-bar
        // occlusion; sm+ uses a generous fixed cap.
        'max-h-[92dvh] sm:max-h-[90vh]',
        // Flex column so the header/footer stay pinned and only the
        // body scrolls — previously the entire modal scrolled, which
        // hid the title and action buttons on long forms.
        'flex flex-col',
        sizeClass,
      )}>
        <div className="flex-shrink-0 flex items-center justify-between px-5 sm:px-6 py-4 border-b border-slate-100 dark:border-slate-800">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
          <button onClick={onClose} className="p-2 -mr-2 rounded hover:bg-slate-100 dark:hover:bg-slate-800" aria-label="Close">
            <X size={18} className="text-slate-500" />
          </button>
        </div>
        <div className="flex-1 px-5 sm:px-6 py-4 overflow-y-auto overscroll-contain">{children}</div>
        {footer && (
          <div className="flex-shrink-0 flex items-center justify-end gap-2 px-5 sm:px-6 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 rounded-b-2xl sm:rounded-b-2xl pb-[max(1rem,env(safe-area-inset-bottom,0px))]">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

// ------------ Confirm Delete Modal ------------
/**
 * A deletion confirmation modal that requires the user to type a word before
 * the Delete button becomes active.
 *
 * Props:
 *   open        – boolean
 *   onClose     – () => void
 *   onConfirm   – () => void | Promise<void>  (called when confirmed)
 *   loading     – boolean  (shows spinner on Delete button while deleting)
 *   title       – string   (modal heading, e.g. "Delete contact")
 *   description – ReactNode (what will be deleted / consequences)
 *   confirmWord – string   (word user must type, defaults to "DELETE")
 */
export const ConfirmDeleteModal = ({
  open,
  onClose,
  onConfirm,
  loading = false,
  title = 'Confirm deletion',
  description,
  confirmWord = 'DELETE',
}) => {
  const [typed, setTyped] = useState('');
  const inputRef = useRef(null);
  const match = typed.trim().toUpperCase() === confirmWord.toUpperCase();

  // Reset input each time the modal opens
  useEffect(() => {
    if (open) {
      setTyped('');
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-xl dark:bg-slate-900">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800">
          <h3 className="text-base font-semibold text-red-600 dark:text-red-400 flex items-center gap-2">
            <AlertCircle size={16} /> {title}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800">
            <X size={18} className="text-slate-500" />
          </button>
        </div>
        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <div className="text-sm text-slate-600 dark:text-slate-300">{description}</div>
          <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-300">
            This action <strong>cannot be undone</strong>.
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              Type <span className="font-mono font-bold text-slate-700 dark:text-slate-200">{confirmWord}</span> to confirm
            </label>
            <input
              ref={inputRef}
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && match) onConfirm(); }}
              placeholder={confirmWord}
              className={classNames(
                'w-full rounded-lg border px-3 py-2 text-sm font-mono transition-colors outline-none',
                'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100',
                match
                  ? 'border-red-400 dark:border-red-600 ring-1 ring-red-300 dark:ring-red-700'
                  : 'border-slate-300 dark:border-slate-700 focus:border-slate-400 dark:focus:border-slate-500',
              )}
            />
          </div>
        </div>
        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 rounded-b-2xl">
          <Button variant="ghost" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button
            tone="danger"
            loading={loading}
            disabled={!match || loading}
            onClick={onConfirm}
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
};

// ------------ KPI card ------------
export const KPI = ({ label, value, delta, icon: Icon, tone = 'indigo', loading = false, title }) => {
  const tones = {
    indigo: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
    green:  'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    amber:  'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    red:    'bg-red-500/10 text-red-600 dark:text-red-400',
  };
  // Hover tooltip — used when the displayed value is compact (e.g. GH₵12K)
  // and the full amount (GH₵12,345) is worth showing on hover. Falls back
  // to the value itself so screen readers / mouseovers still get context.
  const hoverTitle = title ?? (typeof value === 'string' ? value : undefined);
  return (
    <Card className="p-3 sm:p-5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 font-medium leading-tight">{label}</p>
          {loading ? (
            <Skeleton className="mt-2 h-7 w-16" />
          ) : (
            <p
              className="mt-1.5 text-xl sm:text-2xl font-semibold text-slate-900 dark:text-slate-100 truncate"
              title={hoverTitle}
            >
              {value}
            </p>
          )}
          {delta != null && !loading && (
            <p className={classNames('mt-1 text-xs', delta >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
              {delta >= 0 ? '↑' : '↓'} {Math.abs(delta)}%
            </p>
          )}
        </div>
        {Icon && (
          <div className={classNames('rounded-xl p-2 sm:p-2.5 flex-shrink-0', tones[tone])}>
            <Icon size={18} className="sm:hidden" />
            <Icon size={20} className="hidden sm:block" />
          </div>
        )}
      </div>
    </Card>
  );
};

// Shimmer placeholder for use during initial loads. Tailwind's animate-pulse
// is good enough — no need for a custom keyframe. Useful for KPI numbers,
// list rows, profile blocks, etc.
export const Skeleton = ({ className = '' }) => (
  <div className={classNames('animate-pulse rounded bg-slate-200/70 dark:bg-slate-700/50', className)} />
);

// A column of skeleton list rows that approximates a typical "row with
// avatar + 2 lines of text" layout. Used in place of "Loading…" copy
// while a list fetches. Reads better on slow connections — the user
// sees the shape of the page immediately.
export const SkeletonListRows = ({ rows = 6, withAvatar = false }) => (
  <ul className="divide-y divide-slate-100 dark:divide-slate-800">
    {Array.from({ length: rows }).map((_, i) => (
      <li key={i} className="flex items-center gap-3 px-4 sm:px-6 py-4">
        {withAvatar && <Skeleton className="w-9 h-9 rounded-full flex-shrink-0" />}
        <div className="flex-1 min-w-0 space-y-2">
          <Skeleton className="h-3.5 w-1/3" />
          <Skeleton className="h-3 w-1/2" />
        </div>
        <Skeleton className="h-5 w-16 hidden sm:block" />
      </li>
    ))}
  </ul>
);

// HelpTooltip — small "?" icon that reveals a tooltip on hover/focus.
// Use next to less-obvious form labels to give first-time users context
// without cluttering the field with descriptive text. Pure CSS, no
// portals — relies on a sibling :hover/:focus state. Wraps in <span>
// so it inlines cleanly next to a <Label>.
export const HelpTooltip = ({ children, side = 'right' }) => (
  <span className="relative inline-flex items-center group align-middle ml-1">
    <button
      type="button"
      tabIndex={0}
      aria-label="Help"
      className="w-3.5 h-3.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-300 text-[9px] font-bold flex items-center justify-center hover:bg-slate-300 dark:hover:bg-slate-600 transition focus:outline-none focus:ring-2 focus:ring-indigo-500"
    >
      ?
    </button>
    <span
      role="tooltip"
      className={classNames(
        'pointer-events-none absolute z-50 hidden group-hover:block group-focus-within:block',
        'bottom-full mb-2',
        side === 'left'  ? 'right-0'  :
        side === 'right' ? 'left-0'   :
        'left-1/2 -translate-x-1/2',
        'w-64 max-w-xs px-3 py-2 rounded-lg shadow-lg',
        'bg-slate-900 dark:bg-slate-700 text-white text-xs leading-relaxed',
      )}
    >
      {children}
    </span>
  </span>
);

// ------------ Simple SVG chart (no dependency) ------------
export const MiniBarChart = ({ data = [], height = 60, color = '#6366f1' }) => {
  if (!data.length) return null;
  const max = Math.max(...data.map((d) => d.value), 1);
  const barW = 100 / data.length;
  return (
    <svg viewBox={`0 0 100 ${height}`} className="w-full" preserveAspectRatio="none">
      {data.map((d, i) => {
        const h = (d.value / max) * (height - 2);
        return (
          <rect
            key={i}
            x={i * barW + 1}
            y={height - h}
            width={barW - 2}
            height={h}
            fill={color}
            opacity={0.85}
            rx={1}
          >
            <title>{`${d.label}: ${d.value}`}</title>
          </rect>
        );
      })}
    </svg>
  );
};

export const Sparkline = ({ data = [], height = 40, stroke = '#6366f1' }) => {
  if (data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const points = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * 100;
      const y = height - ((v - min) / range) * (height - 2) - 1;
      return `${x},${y}`;
    })
    .join(' ');
  return (
    <svg viewBox={`0 0 100 ${height}`} className="w-full" preserveAspectRatio="none">
      <polyline points={points} fill="none" stroke={stroke} strokeWidth={1.5} />
    </svg>
  );
};

// ── StatusDropdown ────────────────────────────────────────────────────────────
// Reusable inline status-change pill.
// options: [{ value, label, tone }]
// onChange: (newValue) => void   (called only when value actually changes)
export const StatusDropdown = ({ value, options = [], onChange, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const current = options.find((o) => o.value === value) || options[0];

  const openMenu = () => {
    if (disabled) return;
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) {
      setCoords({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="inline-block">
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={openMenu}
        className="inline-flex items-center gap-1 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Badge tone={current?.tone}>{current?.label}</Badge>
        <ChevronDown size={11} className="text-slate-400" />
      </button>
      {open && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: coords.top, left: coords.left, zIndex: 9999 }}
          className="min-w-max rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 py-1"
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { setOpen(false); if (opt.value !== value) onChange(opt.value); }}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2 ${opt.value === value ? 'font-semibold' : ''}`}
            >
              <Badge tone={opt.tone}>{opt.label}</Badge>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};
